<template>
    <div class="fee-economics-section">
        <div class="section-divider">
            <span class="section-title">Fee Economics</span>
        </div>
        <!-- Global Fee Breakdown -->
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Network Fee Breakdown Over Time</div>
                    <div class="panel-subtitle">Base Fees | Priority Fees | Jito Tips</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="globalFeeChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top Programs by Fee Revenue</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="feeRankChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Market Share</div>
                    <div class="panel-subtitle">% of total network fees</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="feeShareChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Revenue Over Time</div>
                    <div class="panel-subtitle">Top programs + Others</div>
                </div>
                <div class="panel-body chart-extra-tall">
                    <canvas ref="feeTimeChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Composition by Program</div>
                    <div class="panel-subtitle">Base | Priority | Tips breakdown</div>
                </div>
                <div class="panel-body">
                    <div class="fee-breakdown-list">
                        <div
                            v-for="p in topByFees"
                            :key="p.program"
                            class="fee-breakdown-item"
                        >
                            <div class="fee-breakdown-label">
                                <a :href="`/analytics/programs/${p.program}`" class="text-gradient">
                                    {{ programLabel(p) }}
                                </a>
                                <span class="fee-total font-mono">{{ p.metrics.totalFees.toFixed(2) }} SOL</span>
                            </div>
                            <div class="fee-breakdown-bar">
                                <div
                                    class="fee-segment fee-segment--base"
                                    :style="{ width: (p.metrics.feeComposition?.base || 0) + '%' }"
                                    :title="`Base: ${p.metrics.feeComposition?.base || 0}%`"
                                ></div>
                                <div
                                    class="fee-segment fee-segment--priority"
                                    :style="{ width: (p.metrics.feeComposition?.priority || 0) + '%' }"
                                    :title="`Priority: ${p.metrics.feeComposition?.priority || 0}%`"
                                ></div>
                                <div
                                    class="fee-segment fee-segment--tips"
                                    :style="{ width: (p.metrics.feeComposition?.tips || 0) + '%' }"
                                    :title="`Tips: ${p.metrics.feeComposition?.tips || 0}%`"
                                ></div>
                            </div>
                        </div>
                        <div class="fee-legend mt-3">
                            <span class="legend-item"><span class="legend-color legend-color--base"></span> Base</span>
                            <span class="legend-item"><span class="legend-color legend-color--priority"></span> Priority</span>
                            <span class="legend-item"><span class="legend-color legend-color--tips"></span> Jito Tips</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Per-Program Jito Analysis -->
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Jito Adoption by Program</div>
                    <div class="panel-subtitle">% of transactions using Jito bundles</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="jitoAdoptionChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Tip Efficiency</div>
                    <div class="panel-subtitle">Average tip per compute unit (lamports)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="tipEfficiencyChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Native vs Jito Comparison</div>
                    <div class="panel-subtitle">Fee/Revenue split by Jito bundle usage</div>
                </div>
                <div class="panel-body">
                    <div class="jito-comparison-list">
                        <div
                            v-for="p in programsWithJito"
                            :key="p.program"
                            class="jito-comparison-item"
                        >
                            <div class="jito-comparison-label">
                                <a :href="`/analytics/programs/${p.program}`" class="text-gradient">
                                    {{ programLabel(p) }}
                                </a>
                                <span class="jito-percent font-mono">{{ p.jitoPercent.toFixed(1) }}% Jito</span>
                            </div>
                            <div class="jito-comparison-bar">
                                <div
                                    class="jito-segment jito-segment--regular"
                                    :style="{ width: (100 - p.jitoPercent) + '%' }"
                                    :title="`Native: ${(100 - p.jitoPercent).toFixed(1)}% (${p.regularTxns.toLocaleString()} txns)`"
                                ></div>
                                <div
                                    class="jito-segment jito-segment--jito"
                                    :style="{ width: p.jitoPercent + '%' }"
                                    :title="`Jito: ${p.jitoPercent.toFixed(1)}% (${p.jitoTxns.toLocaleString()} txns)`"
                                ></div>
                            </div>
                            <div class="jito-comparison-stats">
                                <span class="jito-stat">
                                    <span class="jito-stat-label">Native:</span>
                                    <span class="jito-stat-value font-mono">{{ formatCompact(p.regularTxns) }} txns</span>
                                </span>
                                <span class="jito-stat">
                                    <span class="jito-stat-label">Jito:</span>
                                    <span class="jito-stat-value font-mono text-jito">{{ formatCompact(p.jitoTxns) }} txns</span>
                                </span>
                                <span class="jito-stat">
                                    <span class="jito-stat-label">Tips:</span>
                                    <span class="jito-stat-value font-mono text-jito">{{ p.totalTips.toFixed(2) }} SOL</span>
                                </span>
                            </div>
                        </div>
                        <div class="jito-legend mt-3">
                            <span class="legend-item"><span class="legend-color legend-color--regular"></span> Native</span>
                            <span class="legend-item"><span class="legend-color legend-color--jito"></span> Jito</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Fee Percentile Distributions -->
        <div class="analytics-row cols-1 mb-4" v-if="distributions">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Network Fee Distributions</div>
                    <div class="panel-subtitle">Transaction fee percentiles (lamports)</div>
                </div>
                <div class="panel-body">
                    <div class="percentile-grid">
                        <div class="percentile-card">
                            <div class="percentile-header">Total Fee</div>
                            <div class="percentile-ranges">
                                <div class="percentile-item">
                                    <span class="percentile-label">p50</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.fee?.p50) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p90</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.fee?.p90) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p99</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.fee?.p99) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="percentile-card">
                            <div class="percentile-header">Priority Fee</div>
                            <div class="percentile-ranges">
                                <div class="percentile-item">
                                    <span class="percentile-label">p50</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.priorityFee?.p50) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p90</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.priorityFee?.p90) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p99</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.priorityFee?.p99) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="percentile-card">
                            <div class="percentile-header">Jito Tip</div>
                            <div class="percentile-ranges">
                                <div class="percentile-item">
                                    <span class="percentile-label">p50</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.jitoTip?.p50) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p90</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.jitoTip?.p90) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p99</span>
                                    <span class="percentile-value font-mono">{{ formatLamports(distributions.jitoTip?.p99) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="percentile-card">
                            <div class="percentile-header">Fee per CU</div>
                            <div class="percentile-ranges">
                                <div class="percentile-item">
                                    <span class="percentile-label">p50</span>
                                    <span class="percentile-value font-mono">{{ formatMicroLamports(distributions.feePerCu?.p50) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p90</span>
                                    <span class="percentile-value font-mono">{{ formatMicroLamports(distributions.feePerCu?.p90) }}</span>
                                </div>
                                <div class="percentile-item">
                                    <span class="percentile-label">p99</span>
                                    <span class="percentile-value font-mono">{{ formatMicroLamports(distributions.feePerCu?.p99) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatCompact, formatPubkey, programLabel } from '../../composables/useFormatters';
import { colors, getBarChartOptions, getDoughnutChartOptions, getLineChartOptions, getStackedBarChartOptions } from '../../composables/useChartConfig';
import { GRANULARITY_MODES, transformValue, getUnitSuffix } from '../../composables/useGranularityToggle';

export default {
    name: 'ProgramFeeEconomics',

    props: {
        programs: {
            type: Array,
            required: true
        },
        interval: {
            type: String,
            default: '1h'
        },
        range: {
            type: String,
            default: '24h'
        },
        granularityMode: {
            type: String,
            default: GRANULARITY_MODES.TOTAL
        }
    },

    data() {
        return {
            charts: {},
            colors: colors,
            distributions: null
        };
    },

    computed: {
        topByFees() {
            return [...this.programs]
                .sort((a, b) => b.metrics.totalFees - a.metrics.totalFees)
                .slice(0, 8);
        },
        // Programs with Jito data for comparison
        programsWithJito() {
            return [...this.programs]
                .filter(p => p.metrics.totalTransactions > 0)
                .map(p => {
                    const jitoTxns = p.metrics.jitoTransactions || 0;
                    const totalTxns = p.metrics.totalTransactions || 1;
                    return {
                        program: p.program,
                        name: p.name,
                        jitoTxns,
                        regularTxns: totalTxns - jitoTxns,
                        jitoPercent: (jitoTxns / totalTxns) * 100,
                        totalTips: p.metrics.totalTips || 0,
                        tipPerCu: p.metrics.tipPerCu || 0
                    };
                })
                .sort((a, b) => b.jitoPercent - a.jitoPercent)
                .slice(0, 10);
        },
        // Top programs by Jito adoption for charts
        topByJitoAdoption() {
            return [...this.programs]
                .filter(p => p.metrics.totalTransactions > 100) // Filter out low-volume programs
                .map(p => {
                    const jitoTxns = p.metrics.jitoTransactions || 0;
                    const totalTxns = p.metrics.totalTransactions || 1;
                    return {
                        program: p.program,
                        name: p.name,
                        jitoPercent: (jitoTxns / totalTxns) * 100,
                        tipPerCu: p.metrics.tipPerCu || 0
                    };
                })
                .sort((a, b) => b.jitoPercent - a.jitoPercent)
                .slice(0, 10);
        }
    },

    watch: {
        programs: {
            handler() {
                if (Object.keys(this.charts).length > 0) {
                    this.updateCharts();
                }
            },
            deep: true
        },
        interval() {
            this.fetchFeeDistributions();
        },
        range() {
            this.fetchFeeDistributions();
        },
        granularityMode() {
            if (Object.keys(this.charts).length > 0) {
                this.updateCharts();
            }
        }
    },

    mounted() {
        this.initCharts();
        this.fetchFeeDistributions();
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
    },

    methods: {
        formatCompact,
        formatPubkey,
        programLabel,

        transformForGranularity(value) {
            return transformValue(value, this.granularityMode, this.interval);
        },

        getGranularityLabel(baseLabel) {
            const suffix = getUnitSuffix(this.granularityMode, this.interval, '');
            return suffix ? `${baseLabel} ${suffix}` : baseLabel;
        },

        // Format lamports to readable string
        formatLamports(value) {
            if (value === null || value === undefined) return '-';
            if (value >= 1e9) return formatCompact(value / 1e9) + ' SOL';
            if (value >= 1e6) return formatCompact(value / 1e6) + 'M';
            if (value >= 1e3) return formatCompact(value / 1e3) + 'K';
            return value.toLocaleString();
        },

        // Format micro-lamports (for fee per CU)
        formatMicroLamports(value) {
            if (value === null || value === undefined) return '-';
            // micro-lamports per CU
            if (value >= 1e6) return (value / 1e6).toFixed(2) + ' μL/CU';
            if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
            return value.toFixed(1);
        },

        async fetchFeeDistributions() {
            try {
                const params = new URLSearchParams({
                    interval: this.interval,
                    range: this.range
                });
                const response = await fetch(`/analytics/api/fee-metrics?${params}`);
                const data = await response.json();
                this.distributions = data.distributions || null;
            } catch (error) {
                console.error('Failed to fetch fee distributions:', error);
                this.distributions = null;
            }
        },

        initCharts() {
            // Global Fee Breakdown Chart (Base | Priority | Tips stacked)
            this.charts.globalFee = markRaw(new Chart(this.$refs.globalFeeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getStackedBarChartOptions({ formatCallback: v => v.toFixed(2) }),
                    plugins: {
                        ...getStackedBarChartOptions().plugins,
                        legend: {
                            display: true,
                            position: 'top',
                            labels: {
                                color: '#9ca3af',
                                font: { family: "'JetBrains Mono', monospace" }
                            }
                        }
                    }
                }
            }));

            // Fee Rank Chart
            this.charts.feeRank = markRaw(new Chart(this.$refs.feeRankChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    horizontal: true,
                    showLegend: false,
                    title: 'SOL'
                })
            }));

            // Fee Share Chart
            this.charts.feeShare = markRaw(new Chart(this.$refs.feeShareChart, {
                type: 'doughnut',
                data: { labels: [], datasets: [] },
                options: getDoughnutChartOptions({ legendPosition: 'right' })
            }));

            // Fee Time Chart
            this.charts.feeTime = markRaw(new Chart(this.$refs.feeTimeChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ title: 'SOL' })
            }));

            // Jito Adoption Chart (horizontal bar)
            this.charts.jitoAdoption = markRaw(new Chart(this.$refs.jitoAdoptionChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getBarChartOptions({
                        horizontal: true,
                        showLegend: false
                    }),
                    scales: {
                        x: {
                            max: 100,
                            ticks: {
                                color: '#9ca3af',
                                callback: v => v + '%'
                            },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        },
                        y: {
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        }
                    }
                }
            }));

            // Tip Efficiency Chart (horizontal bar)
            this.charts.tipEfficiency = markRaw(new Chart(this.$refs.tipEfficiencyChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    horizontal: true,
                    showLegend: false,
                    title: 'lamports/CU'
                })
            }));

            this.updateCharts();
        },

        updateCharts() {
            if (!this.programs.length) return;

            const byFees = this.topByFees;
            const topN = [...this.programs]
                .sort((a, b) => b.metrics.totalFees - a.metrics.totalFees)
                .slice(0, 25); // Top 25 for time series
            const timeLabels = topN[0]?.timeSeries?.map(t => t.label) || [];
            const transform = this.transformForGranularity.bind(this);

            // Global Fee Breakdown (Base | Priority | Tips) - aggregate from all programs
            const baseFeeData = timeLabels.map((_, timeIdx) => {
                const total = this.programs.reduce((sum, p) => {
                    const comp = p.timeSeries?.[timeIdx]?.feeComposition;
                    const totalFees = p.timeSeries?.[timeIdx]?.fees || 0;
                    return sum + (totalFees * (comp?.base || 40) / 100); // Estimate base as 40% default
                }, 0);
                return transform(total);
            });
            const priorityFeeData = timeLabels.map((_, timeIdx) => {
                const total = this.programs.reduce((sum, p) => {
                    const comp = p.timeSeries?.[timeIdx]?.feeComposition;
                    const totalFees = p.timeSeries?.[timeIdx]?.fees || 0;
                    return sum + (totalFees * (comp?.priority || 40) / 100); // Estimate priority as 40% default
                }, 0);
                return transform(total);
            });
            const jitoTipsData = timeLabels.map((_, timeIdx) => {
                const total = this.programs.reduce((sum, p) => {
                    const comp = p.timeSeries?.[timeIdx]?.feeComposition;
                    const totalFees = p.timeSeries?.[timeIdx]?.fees || 0;
                    return sum + (totalFees * (comp?.tips || 20) / 100); // Estimate tips as 20% default
                }, 0);
                return transform(total);
            });

            this.charts.globalFee.data = {
                labels: timeLabels,
                datasets: [
                    {
                        label: this.getGranularityLabel('Base Fees'),
                        data: baseFeeData,
                        backgroundColor: this.colors.feeBreakdown[0],
                        borderWidth: 0
                    },
                    {
                        label: this.getGranularityLabel('Priority Fees'),
                        data: priorityFeeData,
                        backgroundColor: this.colors.feeBreakdown[1],
                        borderWidth: 0
                    },
                    {
                        label: this.getGranularityLabel('Jito Tips'),
                        data: jitoTipsData,
                        backgroundColor: this.colors.feeBreakdown[2],
                        borderWidth: 0
                    }
                ]
            };
            this.charts.globalFee.update();

            // Fee Rank - add borderWidth: 0
            this.charts.feeRank.data = {
                labels: byFees.map(p => programLabel(p)),
                datasets: [{
                    label: this.getGranularityLabel('Fees'),
                    data: byFees.map(p => transform(p.metrics.totalFees)),
                    backgroundColor: this.colors.series,
                    borderWidth: 0
                }]
            };
            this.charts.feeRank.update();

            // Fee Share
            this.charts.feeShare.data = {
                labels: byFees.map(p => programLabel(p)),
                datasets: [{
                    data: byFees.map(p => p.metrics.totalFees),
                    backgroundColor: this.colors.series,
                    borderWidth: 0
                }]
            };
            this.charts.feeShare.update();

            // Fee Time - 25 programs + Others with gradient fills
            const othersData = timeLabels.map((_, timeIdx) => {
                const topNTotal = topN.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.fees || 0);
                }, 0);
                const allTotal = this.programs.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.fees || 0);
                }, 0);
                return transform(Math.max(0, allTotal - topNTotal));
            });

            const ctx = this.$refs.feeTimeChart.getContext('2d');
            const datasets = topN.map((p, i) => {
                const color = this.colors.series[i % this.colors.series.length];
                return {
                    label: programLabel(p),
                    data: p.timeSeries?.map(t => transform(t.fees)) || [],
                    borderColor: color,
                    backgroundColor: `${color}33`,
                    fill: true,
                    tension: 0.3
                };
            });

            // Add "Others" category
            if (othersData.some(v => v > 0)) {
                datasets.push({
                    label: 'Others',
                    data: othersData,
                    borderColor: '#4b5563',
                    backgroundColor: '#4b556333',
                    fill: true,
                    tension: 0.3
                });
            }

            this.charts.feeTime.data = {
                labels: timeLabels,
                datasets: datasets
            };
            this.charts.feeTime.update();

            // Jito Adoption - horizontal bar showing % of txns using Jito
            const jitoData = this.topByJitoAdoption;
            this.charts.jitoAdoption.data = {
                labels: jitoData.map(p => programLabel(p)),
                datasets: [{
                    data: jitoData.map(p => p.jitoPercent),
                    backgroundColor: jitoData.map(p =>
                        p.jitoPercent > 50 ? this.colors.jito :
                        p.jitoPercent > 20 ? this.colors.series[4] :
                        this.colors.neutral
                    ),
                    borderWidth: 0
                }]
            };
            this.charts.jitoAdoption.update();

            // Tip Efficiency - horizontal bar showing tip per CU
            const byTipEfficiency = [...this.programs]
                .filter(p => p.metrics.tipPerCu > 0)
                .sort((a, b) => b.metrics.tipPerCu - a.metrics.tipPerCu)
                .slice(0, 10);
            this.charts.tipEfficiency.data = {
                labels: byTipEfficiency.map(p => programLabel(p)),
                datasets: [{
                    data: byTipEfficiency.map(p => p.metrics.tipPerCu),
                    backgroundColor: this.colors.jito,
                    borderWidth: 0
                }]
            };
            this.charts.tipEfficiency.update();
        }
    }
};
</script>

<style scoped>
.chart-tall {
    min-height: 280px;
}

.fee-breakdown-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.fee-breakdown-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.fee-breakdown-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
}

.fee-total {
    color: #9ca3af;
}

.fee-breakdown-bar {
    display: flex;
    height: 16px;
    border-radius: 4px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}

.fee-segment {
    height: 100%;
    transition: width 0.3s ease;
}

.fee-segment--base {
    background: var(--compass-purple, #6366f1);
}

.fee-segment--priority {
    background: var(--compass-pink, #8b5cf6);
}

.fee-segment--tips {
    background: #f97316;
}

.fee-legend {
    display: flex;
    gap: 16px;
    font-size: 0.8rem;
    color: #9ca3af;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.legend-color {
    width: 12px;
    height: 12px;
    border-radius: 2px;
}

.legend-color--base {
    background: var(--compass-purple, #6366f1);
}

.legend-color--priority {
    background: var(--compass-pink, #8b5cf6);
}

.legend-color--tips {
    background: #f97316;
}

.font-mono {
    font-family: 'JetBrains Mono', monospace;
}

.text-gradient {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Percentile displays */
.percentile-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.percentile-card {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 16px;
}

.percentile-header {
    font-size: 0.9rem;
    font-weight: 500;
    color: #e5e7eb;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.percentile-ranges {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.percentile-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.percentile-label {
    font-size: 0.8rem;
    color: #9ca3af;
    min-width: 40px;
}

.percentile-value {
    font-size: 0.85rem;
    color: #8b5cf6;
}

/* Jito comparison styles */
.jito-comparison-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.jito-comparison-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.jito-comparison-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
}

.jito-percent {
    color: #f97316;
    font-weight: 500;
}

.jito-comparison-bar {
    display: flex;
    height: 20px;
    border-radius: 4px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}

.jito-segment {
    height: 100%;
    transition: width 0.3s ease;
}

.jito-segment--regular {
    background: var(--compass-purple, #8b5cf6);
}

.jito-segment--jito {
    background: linear-gradient(90deg, #f97316, #fb923c);
}

.jito-comparison-stats {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
}

.jito-stat {
    display: flex;
    gap: 4px;
}

.jito-stat-label {
    color: #6b7280;
}

.jito-stat-value {
    color: #9ca3af;
}

.text-jito {
    color: #f97316;
}

.legend-color--regular {
    background: var(--compass-purple, #8b5cf6);
}

.legend-color--jito {
    background: linear-gradient(90deg, #f97316, #fb923c);
}

.jito-legend {
    display: flex;
    gap: 16px;
    font-size: 0.8rem;
    color: #9ca3af;
}
</style>
